// react
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGoBack } from 'hooks/useGoBack';
import { ROUTES } from 'router';

// components
import CareCircleMemberInfoPage from '@karehero/llama/pwa/pages/CareCircleMemberInfo/CareCircleMemberInfo';

// hooks
import { useCareCircle } from 'hooks';

// models
import { CareCircleMember, Relationship } from '@karehero/models';

// store
import { useGetAllRelationshipQuery } from 'store/api/relationship';

export const CareCircleMemberInfo = () => {
  // hooks
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { careCircle, careRecipientName } = useCareCircle();
  const { accountID } = useParams();
  const { data: relationships } = useGetAllRelationshipQuery();

  // memos
  const careCircleMember = useMemo(() => {
    if (!careCircle) return;
    const { careCircleMembers = [] } = careCircle;
    return careCircleMembers.find(
      (member: CareCircleMember) => member.id === accountID,
    );
  }, [careCircle, accountID]);

  const memberName = useMemo(() => {
    let name = `${careCircleMember?.account?.firstName || ''}`;
    if (careCircleMember?.account?.lastName) {
      name += ` ${careCircleMember?.account?.lastName}`;
    }
    return name;
  }, [careCircleMember]);

  const careRecipientRelationship = useMemo(
    () =>
      relationships?.find(
        (r: Relationship) =>
          r.id === careCircleMember?.careRecipientAccountRelationshipId,
      ),
    [careCircleMember, relationships],
  );

  const fields = useMemo(
    () => [
      { label: 'Name', value: memberName },
      {
        label: 'Email address',
        value: careCircleMember?.account?.email || '',
      },
      {
        label: `Relationship to ${careRecipientName}`,
        value: careRecipientRelationship?.name || 'No relationship',
      },
    ],
    [
      memberName,
      careCircleMember,
      careRecipientName,
      careRecipientRelationship,
    ],
  );

  return (
    <CareCircleMemberInfoPage
      title={`${memberName || 'Care recipient'}'s Profile`}
      fields={fields}
      onBack={goBack}
    />
  );
};

export default CareCircleMemberInfo;
